'use client'

// React
import { useEffect, useRef, useState } from 'react'

// next-themes
import { useTheme } from 'next-themes'

// Mermaid
import mermaid from 'mermaid'

interface MermaidProps {
  chart?: string
  className?: string
  /** Override font size for all label types. Defaults to 11px. */
  fontSize?: string
  /** Override font size for edge labels only (arrows). Falls back to fontSize. */
  edgeFontSize?: string
}

const DEFAULT_FONT_SIZE = '11px'

function getMermaidConfig(
  isDark: boolean,
  fontSize: string,
): Parameters<typeof mermaid.initialize>[0] {
  const shared = {
    startOnLoad: false,
    securityLevel: 'loose' as const,
    htmlLabels: true,
    fontFamily: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
    flowchart: {
      nodeSpacing: 30,
      rankSpacing: 35,
      padding: 12,
      subGraphTitleMargin: { top: 28, bottom: 18 },
    },
    stateDiagram: {
      nodeSpacing: 30,
      rankSpacing: 35,
    },
  }

  if (isDark) {
    return {
      ...shared,
      theme: 'base' as const,
      themeVariables: {
        background: '#0f172a',
        mainBkg: '#0c2237',
        primaryColor: '#0c2237',
        primaryBorderColor: '#0ea5e9',
        primaryTextColor: '#e2e8f0',
        secondaryColor: '#1e293b',
        secondaryBorderColor: '#38bdf8',
        secondaryTextColor: '#cbd5e1',
        tertiaryColor: '#0f172a',
        tertiaryBorderColor: '#0369a1',
        tertiaryTextColor: '#94a3b8',
        lineColor: '#2dd4bf',
        edgeLabelBackground: 'rgba(30, 41, 59, 0.9)',
        textColor: '#cbd5e1',
        labelTextColor: '#e2e8f0',
        clusterBkg: '#0f172a',
        clusterBorder: '#334155',
        titleColor: '#e2e8f0',
        nodeTextColor: '#e2e8f0',
        errorBkgColor: '#450a0a',
        errorTextColor: '#fca5a5',
        // Sequence diagram
        actorBkg: '#0c2237',
        actorBorder: '#0ea5e9',
        actorTextColor: '#e2e8f0',
        actorLineColor: '#334155',
        signalColor: '#2dd4bf',
        signalTextColor: '#cbd5e1',
        noteBkgColor: '#1e293b',
        noteTextColor: '#e2e8f0',
        noteBorderColor: '#334155',
        activationBkgColor: '#0c2237',
        activationBorderColor: '#0ea5e9',
        sequenceNumberColor: '#94a3b8',
        loopTextColor: '#cbd5e1',
        labelBoxBkgColor: '#0f172a',
        labelBoxBorderColor: '#334155',
        fontFamily: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
        fontSize,
        padding: '12',
      },
    }
  }

  return {
    ...shared,
    theme: 'base' as const,
    themeVariables: {
      background: '#ffffff',
      mainBkg: '#f0f9ff',
      primaryColor: '#f0f9ff',
      primaryBorderColor: '#0284c7',
      primaryTextColor: '#0c4a6e',
      secondaryColor: '#f8fafc',
      secondaryBorderColor: '#38bdf8',
      secondaryTextColor: '#334155',
      tertiaryColor: '#f1f5f9',
      tertiaryBorderColor: '#7dd3fc',
      tertiaryTextColor: '#475569',
      lineColor: '#94a3b8',
      edgeLabelBackground: 'rgba(248, 250, 252, 0.9)',
      textColor: '#334155',
      labelTextColor: '#1e293b',
      clusterBkg: '#f8fafc',
      clusterBorder: '#e2e8f0',
      titleColor: '#0c4a6e',
      nodeTextColor: '#0c4a6e',
      errorBkgColor: '#fef2f2',
      errorTextColor: '#dc2626',
      // Sequence diagram
      actorBkg: '#f0f9ff',
      actorBorder: '#0284c7',
      actorTextColor: '#0c4a6e',
      actorLineColor: '#cbd5e1',
      signalColor: '#0284c7',
      signalTextColor: '#334155',
      noteBkgColor: '#fef9c3',
      noteTextColor: '#713f12',
      noteBorderColor: '#fde047',
      activationBkgColor: '#e0f2fe',
      activationBorderColor: '#0284c7',
      sequenceNumberColor: '#64748b',
      loopTextColor: '#475569',
      labelBoxBkgColor: '#f8fafc',
      labelBoxBorderColor: '#e2e8f0',
      fontFamily: '"Space Grotesk", ui-sans-serif, system-ui, sans-serif',
      fontSize,
      padding: '12',
    },
  }
}

const LABEL_LAYOUT_PROPS = new Set([
  'display',
  'align-items',
  'justify-content',
  'text-align',
  'line-height',
  'vertical-align',
  'width',
  'height',
  'max-width',
  'min-height',
  'white-space',
  'position',
])

/**
 * Post-render CSS injected into the SVG, scoped to its unique id.
 *
 * IMPORTANT: selectors must be scoped to `#<id>` so they don't bleed into
 * other <svg> elements on the page. Unscoped rules corrupt Mermaid's layout
 * measurements for concurrently-rendering diagrams (state nodes balloon to
 * 2000 px, viewBox explodes to 17 000+ px).
 */
function buildNodeLabelStyles(
  scopeId: string,
  fontSize?: string,
  edgeFontSize?: string,
): string {
  const s = `#${scopeId}`
  // HTML inside SVG foreignObjects does NOT inherit font-size from the SVG element
  // (falls back to browser default 16px). Always apply the resolved size explicitly.
  const resolvedSize = fontSize ?? DEFAULT_FONT_SIZE
  const resolvedEdgeSize = edgeFontSize ?? resolvedSize
  const fontSizeRules = `
${s} .nodeLabel,
${s} .nodeLabel p,
${s} .nodeLabel span,
${s} g.stateGroup text,
${s} .stateLabel text {
  font-size: ${resolvedSize} !important;
}
${s} .edgeLabel foreignObject > div,
${s} .edgeLabel foreignObject .edgeLabel,
${s} .edgeLabel p,
${s} .edgeLabel span,
${s} .edgeLabel .label {
  font-size: ${resolvedEdgeSize} !important;
}
${s} .labelBkg,
`
  return `
${fontSizeRules}
${s} .node .label foreignObject,
${s} .edgeLabel foreignObject {
  overflow: visible;
}
${s} .node .label foreignObject > div,
${s} .node .label foreignObject .nodeLabel,
${s} .edgeLabel foreignObject > div,
${s} .edgeLabel foreignObject .edgeLabel,
${s} .nodeLabel,
${s} .edgeLabel .label {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  width: 100% !important;
  height: 100% !important;
  box-sizing: border-box !important;
  text-align: center !important;
  line-height: 1.35 !important;
  margin: 0 !important;
  padding: 0 !important;
}
${s} .nodeLabel p,
${s} .nodeLabel span,
${s} .edgeLabel p,
${s} .edgeLabel span,
${s} .label div,
${s} .label span {
  width: 100%;
  text-align: center !important;
  margin: 0 !important;
}
${s} .node .label text,
${s} .edgeLabel text {
  text-anchor: middle;
  dominant-baseline: middle;
}
${s} .cluster-label text,
${s} .cluster-label span,
${s} .cluster-label div,
${s} .cluster-label foreignObject * {
  font-weight: 700 !important;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
${s} .node rect,
${s} .node polygon {
  rx: 3;
  ry: 3;
}
`.replace(/\s+/g, ' ')
}

/**
 * Serialize mermaid.render calls. Concurrent renders share Mermaid's global
 * state and can corrupt each other's layout measurements.
 */
let renderChain: Promise<unknown> = Promise.resolve()
function enqueueMermaidRender<T>(fn: () => Promise<T>): Promise<T> {
  const run = renderChain.then(fn)
  renderChain = run.then(
    () => undefined,
    () => undefined,
  )
  return run
}

/** Strip inline layout styles Mermaid sets on label wrappers (they fight flex centering). */
function stripForeignObjectLayoutStyles(svg: string): string {
  return svg.replace(
    /<foreignObject\b([^>]*)>([\s\S]*?)<\/foreignObject>/gi,
    (match, attrs, inner) => {
      const cleanedInner = inner.replace(
        /\sstyle="([^"]*)"/gi,
        (_styleAttr: string, styleValue: string) => {
          const cleaned = styleValue
            .split(';')
            .map((rule) => rule.trim())
            .filter((rule) => {
              if (!rule) return false
              const prop = rule.split(':')[0]?.trim().toLowerCase()
              return prop && !LABEL_LAYOUT_PROPS.has(prop)
            })
            .join('; ')
            .trim()
          return cleaned ? ` style="${cleaned}"` : ''
        },
      )
      return `<foreignObject${attrs}>${cleanedInner}</foreignObject>`
    },
  )
}

export function Mermaid({
  chart,
  className = '',
  fontSize,
  edgeFontSize,
}: MermaidProps) {
  const { resolvedTheme } = useTheme()
  const chartRef = useRef<HTMLDivElement>(null)
  const [svg, setSvg] = useState<string>('')
  const [naturalWidth, setNaturalWidth] = useState<string | undefined>(
    undefined,
  )

  useEffect(() => {
    const renderChart = async () => {
      if (typeof window === 'undefined') return
      if (chart == null || chart.trim() === '') {
        setSvg('')
        return
      }

      const isDark = resolvedTheme === 'dark'

      try {
        // Wait for fonts before entering the queue so Mermaid measures at the
        // correct glyph widths. initialize() must be inside the queued fn so
        // concurrent diagram mounts don't overwrite each other's config.
        if (document.fonts?.ready) {
          await document.fonts.ready
        }

        const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`
        const { svg: rendered } = await enqueueMermaidRender(() => {
          mermaid.initialize(
            getMermaidConfig(isDark, fontSize ?? DEFAULT_FONT_SIZE),
          )
          return mermaid.render(id, chart)
        })

        // Extract the natural diagram width Mermaid computed and add breathing room.
        const addCardPadding = (px: number) => px + 100
        const maxWidthMatch = rendered.match(/max-width\s*:\s*([\d.]+)px/)
        setNaturalWidth(
          maxWidthMatch
            ? `${addCardPadding(parseFloat(maxWidthMatch[1]))}px`
            : undefined,
        )

        const fixed = stripForeignObjectLayoutStyles(
          rendered
            // Force overflow:visible so labels near the SVG edge aren't clipped.
            .replace(
              /^(<svg\b[^>]*?)(?:\sstyle="([^"]*)")?/,
              (_, tag, existing) => {
                const base = existing
                  ? existing.replace(/overflow\s*:[^;]+;?/g, '')
                  : ''
                return `${tag} style="${base ? base + '; ' : ''}overflow: visible;"`
              },
            )
            // Inject label-centering CSS scoped to this diagram's id.
            .replace(
              /(<svg\b[^>]*>)/,
              `$1<style>${buildNodeLabelStyles(id, fontSize, edgeFontSize)}</style>`,
            ),
        )

        setSvg(fixed)
      } catch (error) {
        console.error('Error rendering mermaid chart:', error)
        if (chartRef.current) {
          chartRef.current.innerHTML = `<pre class="text-xs text-red-500">${chart ?? ''}</pre>`
        }
      }
    }

    renderChart()
  }, [chart, resolvedTheme, fontSize, edgeFontSize])

  return (
    <div className={`my-8 flex justify-center ${className}`}>
      <div
        ref={chartRef}
        style={{ maxWidth: naturalWidth ?? '100%' }}
        className="w-full rounded-xl border border-slate-200 bg-slate-50 px-6 py-8 dark:border-slate-800 dark:bg-slate-900/50"
        dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
      />
    </div>
  )
}
